import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';

import { Button, Tooltip, useRedeemFlexibleRideModal } from '../..';
import { InfoIcon } from '../../../assets';
import { UBER_TEXT } from '../../../constants/constants';
import {
  FLEXIBLE_REDEEM_SCROLL_ID,
  FLEXIBLE_REDEEM_SCROLL_QUERY,
  shouldShowFlexibleRedeemPanel,
} from '../../../utils/flexibleUberRedeem';

import styles from './index.module.css';

export const FlexibleRideRedeemPanel = ({
  ride,
  role,
  rideId,
  redeemFlexibleUberRide,
  navigate,
  isViewDashboardMode = false,
}) => {
  const location = useLocation();
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemBlocked, setRedeemBlocked] = useState(false);
  const [redeemBlockMessage, setRedeemBlockMessage] = useState(null);

  const shouldShow = useMemo(
    () => shouldShowFlexibleRedeemPanel(ride, role, isViewDashboardMode, redeemBlockMessage),
    [ride, role, isViewDashboardMode, redeemBlockMessage],
  );

  const isRedeemDisabled = redeemBlocked || redeemLoading;

  const tooltipTitle = useMemo(() => {
    if (redeemBlocked) {
      return redeemBlockMessage || UBER_TEXT.REDEEM_NOW_TOOLTIP_DISABLED;
    }

    return UBER_TEXT.REDEEM_NOW_TOOLTIP_ENABLED;
  }, [redeemBlocked, redeemBlockMessage]);

  useEffect(() => {
    setRedeemBlocked(false);
    setRedeemBlockMessage(null);
  }, [rideId]);

  useEffect(() => {
    if (shouldShowFlexibleRedeemPanel(ride, role, isViewDashboardMode, null)) return;

    setRedeemBlocked(false);
    setRedeemBlockMessage(null);
  }, [ride, role, isViewDashboardMode]);

  useEffect(() => {
    if (!shouldShow) return;

    const params = new URLSearchParams(location.search);
    if (params.get(FLEXIBLE_REDEEM_SCROLL_QUERY) !== '1') return;

    const scrollTarget = document.getElementById(FLEXIBLE_REDEEM_SCROLL_ID);
    if (!scrollTarget) return;

    const timer = setTimeout(() => {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);

    return () => clearTimeout(timer);
  }, [location.search, shouldShow]);

  const handleRedeemFlexibleRide = useCallback(async () => {
    setRedeemLoading(true);
    try {
      await redeemFlexibleUberRide({ rideId, Navigation: navigate });
      message.success(UBER_TEXT.REDEEM_RIDE_SUCCESS);
    } catch (error) {
      const data = error?.response?.data;
      const errorMessage =
        (typeof data === 'string' ? data : data?.message) ||
        UBER_TEXT.REDEEM_RIDE_ALREADY_REDEEMED;
      message.error(errorMessage);
      setRedeemBlocked(true);
      setRedeemBlockMessage(errorMessage);
      throw error;
    } finally {
      setRedeemLoading(false);
    }
  }, [rideId, navigate, redeemFlexibleUberRide]);

  const { Modal: RedeemModal, setRedeemModalVisible } = useRedeemFlexibleRideModal({
    id: ride?.seqId,
    onConfirm: handleRedeemFlexibleRide,
    loading: redeemLoading,
  });

  const handleToggleRedeemModal = useCallback(
    () => setRedeemModalVisible(true),
    [setRedeemModalVisible],
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <div id={FLEXIBLE_REDEEM_SCROLL_ID} className={styles.redeemAction}>
        <Button
          disabled={isRedeemDisabled}
          onClick={handleToggleRedeemModal}
          myClassName={styles.redeemCustomBtn}
        >
          {UBER_TEXT.REDEEM_NOW}
        </Button>
        <Tooltip title={<p className={styles.tooltipText}>{tooltipTitle}</p>}>
          <span className={styles.tooltipInfo}>
            <InfoIcon width={30} height={30} className={styles.infoIcon} />
          </span>
        </Tooltip>
      </div>
      {RedeemModal}
    </>
  );
};
